import React, { useEffect, useRef, useState } from "react";
import { View, Image, useWindowDimensions, StyleSheet } from "react-native";
import { useSelector } from "react-redux";
import { Grid, Container, Row, Column } from "ui-grid";
import {
  Accordion,
  AccordionGroup,
  Button,
  fontWeights,
  NavigationCard,
  theme,
} from "ui-components";
import Big from "big.js";
import { PaymentMethodSelection } from "../payment-method-selection/payment-method-selection";
import { PaymentSourceType, PaymentType } from "../../../enums";
import { AddPaymentCCDebit } from "../add-payment-cc-debit/add-payment-cc-debit";
import { AddPaymentBA } from "../add-payment-ba/add-payment-ba";
import { toHumanDollars } from "../../../utils/money";
import { useNavigation } from "@react-navigation/native";
import { addStartOfDayInformationWithTimezone } from "../../../utils/date";
import { translate } from "../../../i18n";
import automationTestingLocators from ".././../../screens/new/utils/automation-testing-locators.json";
import { domusoResponsiveStyleHelper } from "../../../screens/new/utils/domuso-responsive";
import { Spacer } from "../../../components";
import { CheckType } from "../../../enums/check";
import { RootState } from "../../../redux/reducers";
import { dbToAccountTypeFieldValueMapping } from "../../../screens/new/utils/helper";
import toLower from "lodash.tolower";
import upperFirst from "lodash.upperfirst";
import { FullScreenLoader } from "../loader/full-screen-loader";

const { CREDIT_CARD, BANK_ACCOUNT } = PaymentSourceType;

interface OwnProps {
  paymentMethodsAvailabilityObject?: any;
  onPressItem?: any;
  selected?: any;
  currentPropertyFeeProfile?: any;
  paymentSources?: any;
  navigateToCheckScanFlow?: any;
  preSelected?: any;
  paymentDate?: any;
  finalAmounts: {
    amount?: Big;
    platformFee?: Big;
    total?: Big;
  };
  propertyUnit: string;
  reservationId?: string | number;
  currentPropertyProfile?: any;
  paymentSourcesError?: any;
  experianLinkCohort?: boolean;
}

const PaymentMethods = (props: OwnProps) => {
  let {
    paymentMethodsAvailabilityObject,
    onPressItem,
    selected,
    paymentSources,
    preSelected,
    finalAmounts,
    paymentSourcesError,
    paymentDate,
    propertyUnit,
    reservationId,
  } = props;

  const { width: screenWidth } = useWindowDimensions();
  let tooltipWebWidth = screenWidth > 500 ? 350 : screenWidth * 0.7;
  const navigation = useNavigation();
  const propertyFees = useSelector((state: RootState) => state.reservation.propertyFees);

  let [expandedPaymentTypeId, setExpandedPaymentTypeId] = useState();
  let [selectedOptionAccordionId, setSelectedOptionAccordionId] = useState();
  let [toggleForBankBottomSheet, setToggleForBankBottomSheet] = useState(false);
  let [toggleForCardBottomSheet, setToggleForCardBottomSheet] = useState(false);
  const refCCDebit = useRef();
  const refBankAccount = useRef();

  const defaultAccordionCardTitle = translate("paymentMethods.card.title");
  const defaultAccordionBankTitle = translate("paymentMethods.bankAccount.title");
  const platformFeeText = translate("paymentMethods.platformFee");

  const getFeeForPaymentMethods = (platformFee: any) => {
    return platformFee
      ? `${toHumanDollars(platformFee)} ${platformFeeText}`
      : (translate("paymentMethods.noPlatformFee") as string);
  };

  // Platform fees for all payment methods
  const bankAccountCertifiedFee = getFeeForPaymentMethods(propertyFees?.domusoACH?.fee);
  const bankFee = getFeeForPaymentMethods(propertyFees?.bankAccount?.fee);
  const cardFee = propertyFees?.ccDebitCard?.feeValue
    ? (propertyFees?.ccDebitCard?.feeValue * 100).toFixed(2) + `% ${platformFeeText}`
    : translate("paymentMethods.noPlatformFee");
  const moneygramFee = getFeeForPaymentMethods(propertyFees?.moneygram?.fee);
  const checkScanCertifiedFee = getFeeForPaymentMethods(propertyFees?.checkScanCertified?.fee);
  const checkScanPersonalFee = getFeeForPaymentMethods(propertyFees?.checkScanPersonal?.fee);

  let [accordionCardTitle, setAccordionCardTitle] = useState({
    title: defaultAccordionCardTitle,
    subTitle: cardFee,
  });

  let [accordionBankTitle, setAccordionBankTitle] = useState({
    title: defaultAccordionBankTitle,
    subTitle: bankFee,
  });

  const onAccordionPress = (newExpandedId: any) => {
    if (
      newExpandedId == "accordionCard" &&
      (!paymentMethodsAvailabilityObject.ccDebitCard.visible ||
        paymentMethodsAvailabilityObject.ccDebitCard.disabled.length >= 1)
    ) {
      return;
    }
    if (
      newExpandedId == "accordionBank" &&
      (!paymentMethodsAvailabilityObject.bankAccount.visible ||
        paymentMethodsAvailabilityObject.bankAccount.disabled.length >= 1)
    ) {
      return;
    }

    setExpandedPaymentTypeId((currentExpandedId) =>
      currentExpandedId === newExpandedId ? undefined : newExpandedId
    );
  };

  const onOptionSelected = (newExpandedId: any, expandAccordion?: boolean) => {
    if (expandAccordion) onAccordionPress(newExpandedId);
    setSelectedOptionAccordionId(newExpandedId);
  };

  const createTitleForAccordion = (type: any, last_four_digits: any, paymentType: any) => {
    if (paymentType === CREDIT_CARD)
      return `${upperFirst(toLower(type))} ...xxxx ${last_four_digits}`;
    else return `${dbToAccountTypeFieldValueMapping(type)} ...xxxx ${last_four_digits}`;
  };

  const onPressPaymentMethodBankItem = (item: any, expand: boolean = true) => {
    onPressItem(item);
    onOptionSelected("accordionBank", expand);
    setAccordionCardTitle({
      title: defaultAccordionCardTitle,
      subTitle: cardFee,
    });
    setAccordionBankTitle({
      title: item?.nickname
        ? `${item?.nickname.length > 27 ? `${item?.nickname.substring(0, 27)}...` : item?.nickname}`
        : createTitleForAccordion(item?.bank_account_type, item?.last_four_digits, item?.type),
      subTitle: bankFee,
    });
  };

  const onPressPaymentMethodCardItem = (item: any, expand: boolean = true) => {
    onPressItem(item);
    onOptionSelected("accordionCard", expand);
    setAccordionBankTitle({
      title: defaultAccordionBankTitle,
      subTitle: bankFee,
    });
    setAccordionCardTitle({
      title: item?.nickname
        ? `${item?.nickname.length > 27 ? `${item?.nickname.substring(0, 27)}...` : item?.nickname}`
        : createTitleForAccordion(item?.card_type, item?.last_four_digits, item?.type),
      subTitle: cardFee,
    });
  };

  const onPressPaymentMethodCertifiedACH = () => {
    onPressItem({ id: null, type: PaymentType.DOMUSO_CASH, sourceType: BANK_ACCOUNT });

    //Reset CC and BA selection to defaults
    setExpandedPaymentTypeId(null);
    setSelectedOptionAccordionId(null);
    setAccordionCardTitle({
      title: defaultAccordionCardTitle,
      subTitle: cardFee,
    });
    setAccordionBankTitle({
      title: defaultAccordionBankTitle,
      subTitle: bankFee,
    });
  };

  const getPreSelectedPaymentMethod = () => {
    refBankAccount?.current?.close();
    if (selected?.id) {
      if (selected?.paymentType === CREDIT_CARD) {
        onPressPaymentMethodCardItem(selected, expandedPaymentTypeId === "accordionCard");
      }
      if (selected?.paymentType === BANK_ACCOUNT) {
        onPressPaymentMethodBankItem(selected, expandedPaymentTypeId === "accordionBank");
      }
    }
  };

  useEffect(() => {
    getPreSelectedPaymentMethod();
  }, [preSelected]);

  const creditCardPaymentMethod = (paymentSources: any) =>
    (Array.isArray(paymentSources) ? paymentSources : []).filter(
      (payment) => payment.type === CREDIT_CARD && payment?.active
    );

  const bankAccountPaymentMethod = (paymentSources: any) =>
    (Array.isArray(paymentSources) ? paymentSources : []).filter(
      (payment) => payment.type === BANK_ACCOUNT && payment?.active
    );

  let formatDisabledMsgArr = (arrayOfMsg: string[]) => arrayOfMsg.map((el) => "- " + el);
  let createMsgFromArray = (arrayOfMsg: string[]) => formatDisabledMsgArr(arrayOfMsg).join("\n");

  const navigateToCheckScanFlow = (mobileCheckPayType: CheckType) => {
    let shouldDisableNonCertifiedMethods =
      paymentMethodsAvailabilityObject.shouldDisableNonCertifiedMethods;
    const screen = "PaymentFlowPaymentMethodMobileCheckPaymentInstructions";
    return navigation.navigate(screen, {
      ...props,
      checkTypeProp: mobileCheckPayType,
      shouldDisableNonCertifiedMethods,
      amounts: finalAmounts,
    });
  };

  const navigateToMoneygramPaymentMethod = () => {
    navigation.navigate("PaymentFlowPaymentMethodMoneyGram", {
      amounts: finalAmounts,
      ...props,
    });
  };

  return (
    <View {...automationTestingLocators.paymentMethods.allPaymentsContainer}>
      <Grid>
        <Container>
          {paymentSources?.length >= 0 || paymentSourcesError ? (
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={styles.inputRowInner}>
                  <AccordionGroup
                    expandedId={expandedPaymentTypeId}
                    onAccordionPress={onAccordionPress}>
                    {paymentMethodsAvailabilityObject.ccDebitCard.visible ? (
                      <Accordion
                        titleStyle={{ fontWeight: fontWeights.Regular }}
                        accordionStyle={styles.accordionStyle}
                        tooltipTitle={
                          translate("paymentMethods.paymentMethodAvailability") as string
                        }
                        tooltipBody={createMsgFromArray(
                          paymentMethodsAvailabilityObject.ccDebitCard.disabled
                        )}
                        disabled={paymentMethodsAvailabilityObject.ccDebitCard.disabled.length >= 1}
                        title={accordionCardTitle.title}
                        description={cardFee}
                        id={"accordionCard"}
                        expandedId={expandedPaymentTypeId}
                        leftIconName={"card"}
                        leftIconColor={theme.colors.primary}
                        rightIconColor={theme.colors.primary}
                        selectedOptionAccordionId={selectedOptionAccordionId}
                        tooltipWebWidth={tooltipWebWidth}
                        {...automationTestingLocators.paymentMethods.creditCard}>
                        <>
                          {creditCardPaymentMethod(paymentSources)?.length > 0 &&
                            creditCardPaymentMethod(paymentSources).map((card, index) => {
                              return (
                                <View key={card.id}>
                                  <PaymentMethodSelection
                                    paymentMethodDetails={card}
                                    {...card}
                                    {...props}
                                    key={card.id}
                                    onPressItem={(item: any) => onPressPaymentMethodCardItem(item)}
                                  />
                                  <View
                                    style={{
                                      borderColor: theme.colors.mediumGrey,
                                      borderTopWidth: 1,
                                    }}
                                  />
                                </View>
                              );
                            })}
                          <Button
                            showLeftIcon={true}
                            leftIcon="add"
                            onPress={() => {
                              refCCDebit?.current?.open();
                              setToggleForCardBottomSheet(!toggleForCardBottomSheet);
                            }}
                            text={translate("paymentMethods.card.addButton") as string}
                            type={"tertiary"}
                            style={{ justifyContent: "flex-start" }}
                            textStyle={{
                              fontSize: theme.fontSize.title2,
                              fontFamily: "Lato_700Bold",
                            }}
                            disabled={false}
                            {...automationTestingLocators.paymentMethods.addNewCardButton}
                          />
                        </>
                      </Accordion>
                    ) : null}

                    {paymentMethodsAvailabilityObject.domusoACH.visible && (
                      <>
                        <Spacer size="medium" />
                        <NavigationCard
                          disabled={paymentMethodsAvailabilityObject.domusoACH.disabled.length >= 1}
                          onPress={() => onPressPaymentMethodCertifiedACH()}
                          title={translate("paymentMethods.certifiedBankAccount.title") as string}
                          subTitle={`${bankAccountCertifiedFee}`}
                          leftIconName={"certified-ach"}
                          tooltipTitle={
                            translate("paymentMethods.paymentMethodAvailability") as string
                          }
                          tooltipBody={createMsgFromArray(
                            paymentMethodsAvailabilityObject.domusoACH.disabled
                          )}
                          tooltipWebWidth={tooltipWebWidth}
                          {...automationTestingLocators.paymentMethods.certifiedAch}
                        />
                      </>
                    )}

                    {paymentMethodsAvailabilityObject.bankAccount.visible ? (
                      <>
                        <Spacer size="medium" />
                        <Accordion
                          titleStyle={{ fontWeight: fontWeights.Regular }}
                          accordionStyle={styles.accordionStyle}
                          disabled={
                            paymentMethodsAvailabilityObject.bankAccount.disabled.length >= 1
                          }
                          tooltipTitle={
                            translate("paymentMethods.paymentMethodAvailability") as string
                          }
                          tooltipBody={createMsgFromArray(
                            paymentMethodsAvailabilityObject.bankAccount.disabled
                          )}
                          title={accordionBankTitle.title}
                          description={bankFee}
                          id={"accordionBank"}
                          expandedId={expandedPaymentTypeId}
                          leftIconName={"bank-account"}
                          leftIconColor={theme.colors.primary}
                          rightIconColor={theme.colors.primary}
                          selectedOptionAccordionId={selectedOptionAccordionId}
                          tooltipWebWidth={tooltipWebWidth}
                          {...automationTestingLocators.paymentMethods.bankAccount}>
                          <>
                            {bankAccountPaymentMethod(paymentSources)?.length > 0 &&
                              bankAccountPaymentMethod(paymentSources).map((card, index) => {
                                return (
                                  <View key={card.id}>
                                    <PaymentMethodSelection
                                      key={card.id}
                                      paymentMethodDetails={card}
                                      {...card}
                                      {...props}
                                      onPressItem={(item: any) => {
                                        onPressPaymentMethodBankItem(item);
                                      }}
                                    />
                                    <View
                                      style={{
                                        borderColor: theme.colors.mediumGrey,
                                        borderTopWidth: 1,
                                      }}
                                    />
                                  </View>
                                );
                              })}
                            <View>
                              <Button
                                showLeftIcon={true}
                                leftIcon="add"
                                onPress={() => {
                                  refBankAccount?.current?.open();
                                  setToggleForBankBottomSheet(!toggleForBankBottomSheet);
                                }}
                                text={translate("paymentMethods.bankAccount.addButton") as string}
                                type={"tertiary"}
                                style={{ justifyContent: "flex-start" }}
                                textStyle={{
                                  fontSize: theme.fontSize.title2,
                                  fontFamily: "Lato_700Bold",
                                }}
                                disabled={!paymentMethodsAvailabilityObject.bankAccount.visible}
                                {...automationTestingLocators.paymentMethods.addNewBankButton}
                              />
                            </View>
                          </>
                        </Accordion>
                      </>
                    ) : null}
                  </AccordionGroup>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          ) : (
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <FullScreenLoader loading={true} />
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          )}
          {paymentMethodsAvailabilityObject.moneygram.visible && (
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={styles.inputRowInner}>
                  <Spacer size="medium" />
                  <NavigationCard
                    disabled={paymentMethodsAvailabilityObject.moneygram.disabled.length >= 1}
                    onPress={navigateToMoneygramPaymentMethod}
                    title={translate("paymentMethods.moneygram.title") as string}
                    subTitle={moneygramFee}
                    renderLeftIcon={
                      <Image
                        source={
                          paymentMethodsAvailabilityObject.moneygram.disabled.length >= 1
                            ? require("../../../../assets/moneygram-unavailable-logo.png")
                            : require("../../../../assets/moneygram-logo.png")
                        }
                        style={{ height: 24, width: 24 }}
                        resizeMode={"contain"}
                        {...automationTestingLocators.paymentMethods.iconMoneygram}
                      />
                    }
                    tooltipTitle={translate("paymentMethods.paymentMethodAvailability") as string}
                    tooltipBody={createMsgFromArray(
                      paymentMethodsAvailabilityObject.moneygram.disabled
                    )}
                    tooltipWebWidth={tooltipWebWidth}
                    {...automationTestingLocators.paymentMethods.moneygram}
                  />
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          )}
          {paymentMethodsAvailabilityObject.checkScanCertified.visible && (
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={styles.inputRowInner}>
                  <Spacer size="medium" />
                  <NavigationCard
                    onPress={() => {
                      return navigateToCheckScanFlow(CheckType.CERTIFIED);
                    }}
                    title={translate("paymentMethods.moneyOrder.title") as string}
                    subTitle={checkScanCertifiedFee}
                    leftIconName={"money-order"}
                    tooltipTitle={translate("paymentMethods.paymentMethodAvailability") as string}
                    tooltipBody={createMsgFromArray(
                      paymentMethodsAvailabilityObject.checkScanCertified.disabled
                    )}
                    disabled={
                      paymentMethodsAvailabilityObject.checkScanCertified.disabled.length >= 1
                    }
                    tooltipWebWidth={tooltipWebWidth}
                    {...automationTestingLocators.paymentMethods.moneyOrder}
                  />
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          )}
          {paymentMethodsAvailabilityObject.checkScanPersonal.visible && (
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                <View style={styles.inputRowInner}>
                  <Spacer size="medium" />
                  <NavigationCard
                    onPress={() => {
                      return navigateToCheckScanFlow(CheckType.PERSONAL);
                    }}
                    title={translate("paymentMethods.personalCheck.title") as string}
                    subTitle={checkScanPersonalFee}
                    leftIconName={"personal-check"}
                    tooltipTitle={translate("paymentMethods.paymentMethodAvailability") as string}
                    tooltipBody={createMsgFromArray(
                      paymentMethodsAvailabilityObject.checkScanPersonal.disabled
                    )}
                    disabled={
                      paymentMethodsAvailabilityObject.checkScanPersonal.disabled.length >= 1
                    }
                    tooltipWebWidth={tooltipWebWidth}
                    {...automationTestingLocators.paymentMethods.personalCheck}
                  />
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
            </Row>
          )}
        </Container>
      </Grid>
      <AddPaymentCCDebit
        ref={refCCDebit}
        {...props}
        fromSelectPayment={true}
        isDomusoCash={true}
        toggleForCardBottomSheet={toggleForCardBottomSheet}
        updated={() => {}}
      />
      <AddPaymentBA
        ref={refBankAccount}
        {...props}
        fromSelectPayment={true}
        toggleForBankBottomSheet={toggleForBankBottomSheet}
        updated={() => {}}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  accordionStyle: {},
  inputRow: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      webDesktop: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 40,
        paddingRight: 40,

        backgroundColor: "white",

        borderLeftWidth: 1,
        borderLeftColor: "rgba(102, 102, 102, 0.1)",
        borderRightWidth: 1,
        borderRightColor: "rgba(102, 102, 102, 0.1)",
      },
    }),
  },
  inputRowInner: {
    ...domusoResponsiveStyleHelper({
      webPhone: {},
      webTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      webDesktop: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      iosPhone: {},
      iosTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
      androidPhone: {},
      androidTablet: {
        paddingLeft: 30,
        paddingRight: 30,
      },
    }),
  },
});
export default PaymentMethods;
